import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, doc, getDoc, onSnapshot, orderBy, query, runTransaction, setDoc } from "firebase/firestore";
import { database } from "../../database/firebase";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { initQuestions } from "../../functions/setUserQuestions";
import {
  createInitialConnect4State,
  GAME_MODE,
  P1,
  P2,
} from "../../game/connectFour";
import "./waiting.css";

const TOTAL_USER_COUNT_KEY = "mythFactTotalUserCount";
const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";

const CHARACTER_SLOTS = ["c1", "c2", "c3", "c4", "c5", "c6", "c7", "c8", "c9"];

function sortLiveUsersForCharacterAssign(users) {
  return [...users].sort((a, b) => {
    const ta = Number(a?.timestamp ?? 0);
    const tb = Number(b?.timestamp ?? 0);
    if (ta !== tb) return ta - tb;
    return String(a?.userId ?? a?.id ?? "").localeCompare(
      String(b?.userId ?? b?.id ?? "")
    );
  });
}

function characterForLobbySlotIndex(slotIndex) {
  return CHARACTER_SLOTS[
    Math.min(Math.max(Number(slotIndex) || 0, 0), CHARACTER_SLOTS.length - 1)
  ];
}

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function normalizeTotalUserCount(value) {
  const n = Number(value);
  if (n === 2) return 2;
  return 2;
}

function resolveLobbyName(locationState, userData) {
  const fromState =
    typeof locationState?.lobbyName === "string" ? locationState.lobbyName.trim() : "";
  if (fromState) return fromState;
  try {
    const fromSession = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (fromSession && fromSession.trim()) return fromSession.trim();
  } catch {
    // ignore storage failures
  }
  return String(userData?.lobby ?? "").trim();
}

function stripNullWords(value) {
  return String(value ?? "")
    .replace(/\bnull\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function getDisplayName(userData) {
  const name = stripNullWords(userData?.name);
  if (name) return name;
  const first = stripNullWords(userData?.firstName);
  if (first) return first;
  const last = stripNullWords(userData?.lastName);
  if (last) return last;
  return `${first} ${last}`.trim();
}

function normalizeCharacterName(character) {
  const value = String(character || "").trim().toLowerCase();
  return /^c[1-9]$/.test(value) ? value : "";
}

function characterAvatarSrc(character) {
  const id = normalizeCharacterName(character);
  return id ? `/img/${id}.png` : "";
}

function isPresenterValue(value) {
  if (value === true) return true;
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
}

function getPlayerLabel(player, fallbackUserData, order) {
  const fromName = stripNullWords(player?.name);
  if (fromName) return fromName;

  const first = stripNullWords(player?.firstName ?? player?.first_name);
  const last = stripNullWords(player?.lastName ?? player?.last_name);
  const fullName = `${first} ${last}`.trim();
  if (fullName) return fullName;

  const fromEmail = String(player?.email ?? "").trim();
  if (fromEmail) return fromEmail;

  const fromEmpId = String(player?.emp_id ?? player?.employeeId ?? "").trim();
  if (fromEmpId) return fromEmpId;

  const fallbackName = stripNullWords(fallbackUserData?.name);
  if (fallbackName) return fallbackName;

  const fallbackEmail = String(fallbackUserData?.email ?? "").trim();
  if (fallbackEmail) return fallbackEmail;

  const fallbackEmpId = String(
    fallbackUserData?.emp_id ?? fallbackUserData?.employeeId ?? ""
  ).trim();
  if (fallbackEmpId) return fallbackEmpId;

  return `Player ${order}`;
}

export default function Waiting() {
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const themeData = useSelector((state) => state.theme?.data);
  const themeCurrentName = useSelector((state) => state.theme?.currentTheme);
  const userData = useMemo(() => readStoredUserData(), []);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [isPresenter, setIsPresenter] = useState(() => isPresenterValue(userData?.presenter));
  const [questionsInitialized, setQuestionsInitialized] = useState(false);
  const [gameStatus, setGameStatus] = useState("");
  const [maxLobbyPlayers, setMaxLobbyPlayers] = useState(() => {
    try {
      const stored = sessionStorage.getItem(TOTAL_USER_COUNT_KEY);
      if (stored != null) return normalizeTotalUserCount(stored);
    } catch {
      // ignore storage failures
    }
    return normalizeTotalUserCount(userData?.totalUserCount);
  });

  const userId = String(userData?.userId ?? userData?.userid ?? userData?.id ?? "").trim();
  const sessionId = String(userData?.sessionId ?? "").trim();
  const lobbyName = useMemo(
    () => resolveLobbyName(location.state, userData),
    [location.state, userData]
  );
  const displayName = getDisplayName(userData);

  useEffect(() => {
    if (!lobbyName) return;
    try {
      sessionStorage.setItem(CURRENT_LOBBY_KEY, lobbyName);
    } catch {
      // ignore storage failures
    }
    if (String(userData?.lobby ?? "").trim() === lobbyName) return;
    try {
      const merged = { ...(userData || {}), lobby: lobbyName };
      const payload = JSON.stringify(merged);
      localStorage.setItem("userData", payload);
      sessionStorage.setItem("userData", payload);
    } catch {
      // ignore storage failures
    }
  }, [lobbyName, userData]);

  useEffect(() => {
    if (questionsInitialized) return;
    if (!userId || !sessionId || !lobbyName) return;

    const organizationId = String(userData?.organizationId ?? "").trim();
    const themeName = String(themeData?.themename ?? themeCurrentName ?? "").trim();
    if (!adminToken || !organizationId || !themeName) return;

    let cancelled = false;

    const initializeQuestions = async () => {
      try {
        await initQuestions({
          userId,
          sessionId,
          organizationId,
          email: userData?.email || "",
          fullName: displayName,
          themeName,
          token: adminToken,
          lobby: lobbyName,
          link: "",
          loginPin: "",
        });
        if (!cancelled) setQuestionsInitialized(true);
      } catch (e) {
        console.error("Failed to initialize questions on waiting page:", e);
      }
    };

    initializeQuestions();

    return () => {
      cancelled = true;
    };
  }, [
    adminToken,
    displayName,
    lobbyName,
    questionsInitialized,
    sessionId,
    themeCurrentName,
    themeData?.themename,
    userData?.email,
    userData?.organizationId,
    userId,
  ]);

  const handlePlayNow = async () => {
    if (starting) return;
    setError("");

    if (!isPresenter) {
      setError("Only presenter can start the game.");
      return;
    }

    if (players.length !== maxLobbyPlayers) {
      setError("Waiting for all players to join...");
      return;
    }

    if (!userId || !sessionId || !lobbyName) {
      setError("Missing lobby details. Please join the lobby again.");
      return;
    }

    try {
      setStarting(true);

      const gameDocRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);
      const connect4Ref = doc(
        database,
        `lobbies/${sessionId}/lobby/${lobbyName}/state/connect4`
      );

      await runTransaction(database, async (tx) => {
        const lobbySnap = await tx.get(gameDocRef);
        const lobbyData = lobbySnap.exists() ? lobbySnap.data() || {} : {};
        const existingStatus = String(lobbyData?.status ?? "")
          .trim()
          .toLowerCase();
        if (existingStatus === "play") return;

        const liveUsers = players.slice(0, maxLobbyPlayers);
        const ids = liveUsers
          .map((p) => String(p?.userId || "").trim())
          .filter(Boolean);
        if (ids.length !== maxLobbyPlayers) {
          throw new Error("Waiting for all players to join...");
        }

        const fresh = createInitialConnect4State({
          player1UserId: ids[0],
          player2UserId: ids[1],
          mode: GAME_MODE.MULTIPLAYER,
        });

        tx.set(connect4Ref, fresh, { merge: true });

        tx.set(
          doc(database, `lobbies/${sessionId}/lobby/${lobbyName}/users/${ids[0]}`),
          { connect4Player: P1, discColor: "red" },
          { merge: true }
        );
        tx.set(
          doc(database, `lobbies/${sessionId}/lobby/${lobbyName}/users/${ids[1]}`),
          { connect4Player: P2, discColor: "yellow" },
          { merge: true }
        );

        tx.set(
          gameDocRef,
          {
            status: "play",
            gameType: "connect4",
            totalUserCount: maxLobbyPlayers,
            startedAtMs: Date.now(),
          },
          { merge: true }
        );
      });

      navigate("/game", { replace: true });
    } catch (e) {
      console.error("Play Now failed:", e);
      setError(String(e?.message || e || "Failed to start game."));
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (!userId || !sessionId || !lobbyName) {
      setError("Missing lobby details. Please join the lobby again.");
      setLoading(false);
      return;
    }

    let unsubscribeUsers = null;
    let unsubscribeGame = null;
    let isActive = true;
    const usersPath = `lobbies/${sessionId}/lobby/${lobbyName}/users`;
    const usersRef = collection(database, usersPath);
    const userDocRef = doc(database, `${usersPath}/${userId}`);
    const gameDocRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);

    const joinCurrentUser = async () => {
      try {
        if (!isActive) return;
        const existingUserDoc = await getDoc(userDocRef);
        if (!existingUserDoc.exists()) {
          const presenter = isPresenterValue(userData?.presenter);
          await setDoc(
            userDocRef,
            {
              userId,
              sessionId,
              lobby: lobbyName,
              organizationId: userData?.organizationId || "",
              name: displayName,
              email: userData?.email || "",
              emp_id: userData?.emp_id || userData?.employeeId || "",
              employeeId: userData?.employeeId || userData?.emp_id || "",
              timestamp: Date.now(),
              status: "live",
              presenter,
            },
            { merge: true }
          );
          if (isActive) setIsPresenter(presenter);
        } else {
          // Keep existing user fields, but mark this user as active on the waiting page.
          const existingPresenter = isPresenterValue(existingUserDoc.data()?.presenter);
          await setDoc(userDocRef, { status: "live" }, { merge: true });
          if (isActive) setIsPresenter(existingPresenter);
        }
      } catch (e) {
        if (isActive) {
          console.error("Failed to upsert current lobby user:", e);
        }
      }
    };

    joinCurrentUser();

    unsubscribeUsers = onSnapshot(
      query(usersRef, orderBy("timestamp", "asc")),
      (snap) => {
        if (!isActive) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const sortedLive = sortLiveUsersForCharacterAssign(
          list.filter((p) => p?.status === "live")
        );
        // Show only users currently on the waiting page.
        setPlayers(sortedLive.slice(0, maxLobbyPlayers));

        const myIdx = sortedLive.findIndex(
          (p) => String(p?.userId || "").trim() === userId
        );
        if (myIdx !== -1) {
          const desired = characterForLobbySlotIndex(myIdx);
          const me = sortedLive[myIdx];
          if (normalizeCharacterName(me.character) !== desired) {
            setDoc(userDocRef, { character: desired }, { merge: true }).catch((err) => {
              console.error("Failed to sync waiting lobby character:", err);
            });
          }
        }

        setLoading(false);
      },
      (e) => {
        if (!isActive) return;
        console.error("Waiting lobby snapshot failed:", e);
        setError("Could not load lobby players.");
        setLoading(false);
      }
    );

    // Navigate everyone to /game when the host starts the match.
    unsubscribeGame = onSnapshot(
      gameDocRef,
      (snap) => {
        if (!isActive || !snap.exists()) return;
        const data = snap.data() || {};
        const status = String(data?.status ?? "").trim().toLowerCase();
        const resolvedTotalUserCount = normalizeTotalUserCount(data?.totalUserCount);
        setMaxLobbyPlayers(resolvedTotalUserCount);
        try {
          sessionStorage.setItem(TOTAL_USER_COUNT_KEY, String(resolvedTotalUserCount));
        } catch {
          // ignore storage write failures
        }
        setGameStatus(status);
      },
      (e) => {
        if (!isActive) return;
        console.error("Waiting game status snapshot failed:", e);
      }
    );

    return () => {
      isActive = false;
      if (unsubscribeUsers) unsubscribeUsers();
      if (unsubscribeGame) unsubscribeGame();
    };
  }, [displayName, lobbyName, navigate, sessionId, userData?.email, userData?.organizationId, userId, maxLobbyPlayers]);

  useEffect(() => {
    if (gameStatus === "play") {
      navigate("/game", { replace: true });
    }
  }, [gameStatus, navigate]);

  const slots = useMemo(() => {
    const filled = players.map((p, idx) => ({
      joined: true,
      label: getPlayerLabel(p, String(p?.userId || "") === userId ? userData : null, idx + 1),
      order: idx + 1,
      isMe: String(p?.userId || "") === userId,
      character: normalizeCharacterName(p?.character),
    }));
    while (filled.length < maxLobbyPlayers) {
      filled.push({
        joined: false,
        label: "",
        order: filled.length + 1,
        isMe: false,
        character: "",
      });
    }
    return filled;
  }, [maxLobbyPlayers, players, userData, userId]);

  const allPlayersJoined = players.length >= maxLobbyPlayers;
  const canStartGame = isPresenter && allPlayersJoined;

  useEffect(() => {
    document.documentElement.classList.add("waiting-static-bg");
    document.body.classList.add("waiting-static-bg");
    return () => {
      document.documentElement.classList.remove("waiting-static-bg");
      document.body.classList.remove("waiting-static-bg");
    };
  }, []);

  const renderPlayerSlot = (slot) => {
    const displayName =
      slot.joined && slot.label && !/^Player\s+\d+$/i.test(String(slot.label).trim())
        ? slot.label
        : slot.joined
          ? `Player ${slot.order}`
          : "Searching...";
    const avatarSrc = characterAvatarSrc(slot.character);

    const statusClass = !slot.joined
      ? "waiting-player-status--pending"
      : slot.isMe
        ? "waiting-player-status--red"
        : "waiting-player-status--yellow";

    return (
      <article
        className={[
          "waiting-player-card",
          slot.joined ? "is-joined" : "is-searching",
          slot.joined && slot.isMe ? "is-red" : "",
          slot.joined && !slot.isMe ? "is-yellow" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div
          className={`waiting-player-avatar ${slot.joined ? "" : "waiting-player-avatar--empty"}`}
        >
          {slot.joined && avatarSrc ? (
            <img src={avatarSrc} alt="" />
          ) : (
            <span className="waiting-player-silhouette" aria-hidden="true" />
          )}
        </div>
        <p className="waiting-player-name">{displayName}</p>
        <span
          className={`waiting-player-status ${statusClass}`}
          aria-hidden="true"
        />
      </article>
    );
  };

  return (
    <div className="waiting-page">
      <div className="waiting-shell">
        <button
          type="button"
          className="waiting-back"
          onClick={() => navigate("/lobby")}
          aria-label="Back to lobby"
        >
          ←
        </button>

        <header className="waiting-header">
          <h1 className="waiting-title">WAITING FOR OPPONENT...</h1>
          <div className="waiting-dots" aria-hidden="true">
            <span className="waiting-dot waiting-dot--active" />
            <span className="waiting-dot" />
            <span className="waiting-dot" />
          </div>
        </header>

        {loading ? <p className="waiting-message">Loading lobby...</p> : null}
        {error ? <p className="waiting-error">{error}</p> : null}

        <div className="waiting-matchup">
          {renderPlayerSlot(slots[0])}
          <div className="waiting-vs" aria-hidden="true">
            VS
          </div>
          {renderPlayerSlot(slots[1])}
        </div>

        <div className="waiting-tip">
          <span className="waiting-tip-icon" aria-hidden="true">
            💡
          </span>
          <p>Tip: You can share the lobby PIN to find your opponent faster!</p>
        </div>

        <div className="waiting-cta-area">
          {canStartGame ? (
            <button
              type="button"
              className="waiting-primary-btn waiting-primary-btn--start"
              disabled={starting}
              onClick={handlePlayNow}
            >
              {starting ? "Starting..." : "START GAME"}
            </button>
          ) : (
            <button
              type="button"
              className="waiting-primary-btn waiting-primary-btn--waiting"
              disabled
            >
              <span className="waiting-primary-icon" aria-hidden="true">
                👥
              </span>
              <span className="waiting-primary-label">WAITING FOR OPPONENT...</span>
              <span className="waiting-primary-spinner" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
